import React, { useState, useEffect } from "react";
import "../../Styles/UpdateFaculty.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../Components/Loader";

function UpdateStudents() {
  const [activeTab, setActiveTab] = useState("pending");
  const [showModal, setShowModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [pendingData, setPendingData] = useState([]);
  const [completedData, setCompletedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (activeTab === "pending") {
      fetchPendingData();
    } else {
      fetchCompletedData();
    }
  }, [activeTab]);

  const fetchPendingData = async () => {
    const authToken = localStorage.getItem("authToken");
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        "https://api.uniums.com/api/admin/student/pending",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch pending student data.");
      }

      const result = await response.json();
      setPendingData(
        result.data.map((student) => ({ ...student, status: "pending" }))
      );
    } catch (err) {
      setError(err.message);
      toast.error(err.message || "Error fetching pending data.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const fetchPendingCount = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          "https://api.uniums.com/api/admin/student/pending/count",
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch pending student count.");
        }

        const result = await response.json();
        setPendingCount(result.count);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPendingCount();
  }, []);

  const fetchCompletedData = async () => {
    const authToken = localStorage.getItem("authToken");
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        "https://api.uniums.com/api/admin/student/completed",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch completed student data.");
      }

      const result = await response.json();
      setCompletedData(
        result.data.map((student) => ({ ...student, status: "completed" }))
      );
    } catch (err) {
      setError(err.message);
      toast.error(err.message || "Error fetching completed data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentDetails = async (email) => {
    try {
      const response = await fetch(
        `https://api.uniums.com/api/admin/student/details?email=${email}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch faculty details.");
      }

      const result = await response.json();
      return result.data;
    } catch (err) {
      toast.error(err.message || "Error fetching faculty details.");
      return null;
    }
  };

  const fetchAdminData = async () => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      setError("No authentication token found.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        "https://api.uniums.com/api/admin/admin/data",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch admin data.");
      }

      const result = await response.json();

      if (result && result.classes && result.subjects && result.semesters) {
        return result;
      } else {
        throw new Error("No valid admin data found.");
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message || "Error fetching admin data.");
      return null;
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleAddDetailsClick = async (student) => {
    setLoading(true);
    try {
      const adminData = await fetchAdminData();
      if (adminData) {
        let existingDetails = null;
        if (student.status === "completed") {
          // Fetch faculty details for completed faculty
          const studentDetails = await fetchStudentDetails(student.email);

          if (studentDetails) {
            existingDetails = {
              selectedClasses: studentDetails.selectedClasses || [],
              selectedSubjects: studentDetails.selectedSubjects || [],
              address: studentDetails.address || "",
              pinCode: studentDetails.pinCode || "",
              state: studentDetails.state || "",
            };
          }
        }

        setSelectedStudent({
          ...student,
          adminData: { ...adminData },
          selectedClasses: existingDetails?.selectedClasses || [],
          selectedSubjects: existingDetails?.selectedSubjects || [],
          address: existingDetails?.address || "",
          pinCode: existingDetails?.pinCode || "",
          state: existingDetails?.state || "",
        });
      }
      setShowModal(true);
    } catch (error) {
      toast.error(error.message || "Error fetching admin data.");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (e, type) => {
    const { name, checked } = e.target;
    setSelectedStudent((prevState) => {
      const updatedSelection = { ...prevState };
      if (type === "classes") {
        if (checked) {
          updatedSelection.selectedClasses.push(name);
        } else {
          updatedSelection.selectedClasses =
            updatedSelection.selectedClasses.filter((item) => item !== name);
        }
      }
      if (type === "subjects") {
        if (checked) {
          updatedSelection.selectedSubjects.push(name);
        } else {
          updatedSelection.selectedSubjects =
            updatedSelection.selectedSubjects.filter((item) => item !== name);
        }
      }
      return updatedSelection;
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedStudent(null);
  };

  const dataToShow = activeTab === "pending" ? pendingData : completedData;

  const handleSaveChanges = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        "https://api.uniums.com/api/admin/student/save",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify({
            name: selectedStudent.name,
            email: selectedStudent.email,
            employeeCode: selectedStudent.employeeCode,
            address: selectedStudent.address,
            pinCode: selectedStudent.pinCode,
            state: selectedStudent.state,
            selectedClasses: selectedStudent.selectedClasses,
            selectedSubjects: selectedStudent.selectedSubjects,
          }),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to save student details");
      }

      const result = await response.json();
      toast.success(result.message || "student details saved successfully.");
      setPendingData((prevData) =>
        prevData.map((student) =>
          student.email === selectedStudent.email
            ? { ...student, status: "completed" }
            : student
        )
      );
      handleCloseModal();
    } catch (error) {
      toast.error(error.message || "Error saving faculty details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="update-faculty-container">
      <h1>Update Students</h1>

      <div className="tabs">
        <button
          className={activeTab === "pending" ? "active-tab" : ""}
          onClick={() => handleTabClick("pending")}
        >
          Pending
        </button>
        <p className="count_pending">
          <strong>{pendingCount}</strong>
        </p>
        <button
          className={activeTab === "completed" ? "active-tab" : ""}
          onClick={() => handleTabClick("completed")}
        >
          Completed
        </button>
      </div>
      {loading && <Loader />}
      {loading && <p className="p_load">Loading...</p>}
      {error && <p className="error">{error}</p>}

      {!loading && !error && (
        <div className="admin-table-container">
          <table className="faculty-table">
            <thead>
              <tr>
                <th>SL</th>
                <th>Name</th>
                <th>Email</th>
                <th>User Code</th>
                <th>User Code</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {dataToShow.length > 0 ? (
                dataToShow.map((student, index) => (
                  <tr key={student._id}>
                    <td>{index + 1}</td>
                    <td>{student.name}</td>
                    <td>{student.email}</td>
                    <td>{student.role}</td>
                    <td>{student.employeeCode}</td>
                    <td>
                      {student.status === "completed" ? (
                        <button
                          className="action_button completed_button"
                          onClick={() => handleAddDetailsClick(student)}
                        >
                          Completed
                        </button>
                      ) : (
                        <button
                          className="action_button"
                          onClick={() => handleAddDetailsClick(student)}
                        >
                          Add Details
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3">No data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal_overlay1">
          <div className="modal_new">
            <div className="modal-header">
              <h2>Add Details {selectedStudent.name}</h2>
              <button
                className="close_button close_button_1"
                onClick={handleCloseModal}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              {selectedStudent && (
                <form>
                  <div className="form_group row_layout">
                    <div className="field">
                      <label>Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedStudent.name}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="field">
                      <label>Email</label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedStudent.email}
                        readOnly
                        disabled
                      />
                    </div>
                    <div className="field">
                      <label>Employee Code</label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedStudent.employeeCode}
                        readOnly
                        disabled
                      />
                    </div>
                  </div>

                  {selectedStudent.adminData && (
                    <>
                      <div className="form_group">
                        <label>Classes</label>
                        <div className="checkbox_group">
                          {selectedStudent.adminData.classes.map((cls, idx) => (
                            <label key={idx} className="checkbox_item">
                              <input
                                type="checkbox"
                                name={cls}
                                checked={selectedStudent.selectedClasses.includes(
                                  cls
                                )}
                                onChange={(e) =>
                                  handleCheckboxChange(e, "classes")
                                }
                              />
                              {cls}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="form_group">
                        <label>Subjects</label>
                        <div className="checkbox_group">
                          {selectedStudent.adminData.subjects.map(
                            (subject, idx) => (
                              <label key={idx} className="checkbox_item">
                                <input
                                  type="checkbox"
                                  name={subject}
                                  checked={selectedStudent.selectedSubjects.includes(
                                    subject
                                  )}
                                  onChange={(e) =>
                                    handleCheckboxChange(e, "subjects")
                                  }
                                />
                                {subject}
                              </label>
                            )
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="form_group">
                    <label>Address</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter address"
                      value={selectedStudent.address || ""}
                      onChange={(e) =>
                        setSelectedStudent({
                          ...selectedStudent,
                          address: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="form_group">
                    <label>Pin Code</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter pin code"
                      value={selectedStudent.pinCode || ""}
                      onChange={(e) =>
                        setSelectedStudent({
                          ...selectedStudent,
                          pinCode: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="form_group">
                    <label>State</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter state"
                      value={selectedStudent.state || ""}
                      onChange={(e) =>
                        setSelectedStudent({
                          ...selectedStudent,
                          state: e.target.value,
                        })
                      }
                    />
                  </div>
                </form>
              )}
            </div>
            <div className="modal-footer">
              <button className="save_button" onClick={handleSaveChanges}>
                Save Changes
              </button>
              <button className="close_button" onClick={handleCloseModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UpdateStudents;
